import clsx from 'clsx'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAddressSuggest, type AddressSuggestion } from '@/hooks/useAddressSuggest'

interface AddressInputProps {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  /** Текст ошибки. Подсвечивает поле красным и выводит сообщение под ним. */
  error?: string
  /**
   * «Известные» города (например, активные строки `rates_k1`). Используются
   * саджестом для двустрочной раскладки `displayName` и для определения
   * города выбранной подсказки. Передавать опционально — если не задано,
   * раскладка будет работать по эвристике, без распознавания locality.
   */
  knownCities?: string[]
  /**
   * Колбэк при выборе подсказки. Передаёт распознанные данные адреса —
   * прежде всего, `locality` (город), чтобы родитель мог автоматически
   * заполнить отдельный селект «Город выкупа».
   */
  onSuggestionPicked?: (item: AddressSuggestion) => void
}

/**
 * Поле адреса с саджестом Яндекс.Карт. По стилю и поведению повторяет
 * мобильный клиент (`stryzh/lib/services/yandex_address_service.dart` +
 * `create_order_sheet.dart`):
 *
 * - В выпадающем списке каждая подсказка — две строки: верх «улица + дом»,
 *   низ «город, регион».
 * - При выборе подсказки в инпут попадает только «улица + дом» (`line1`),
 *   а распознанный город уходит в родителя через `onSuggestionPicked`.
 * - Реки, озёра, горы, парки и пр. отфильтрованы хуком `useAddressSuggest`.
 *
 * Внешне — тот же `flex-col` контейнер с `.stryzh-input-label` и
 * `.stryzh-input-field`, что и обычный `<Input>`, чтобы строки в дроверах
 * не «прыгали» по высоте.
 */
export function AddressInput({
  label,
  value,
  onChange,
  placeholder,
  required,
  error,
  knownCities,
  onSuggestionPicked,
}: AddressInputProps) {
  const { t } = useTranslation()
  const { suggest, suggestions, loading, clear, isConfigured } = useAddressSuggest({
    knownCities,
  })
  const [showList, setShowList] = useState(false)

  const showSuggestions = showList && suggestions.length > 0

  function handleChange(text: string) {
    onChange(text)
    suggest(text)
    setShowList(true)
  }

  function handleSelect(item: AddressSuggestion) {
    // Как в мобильном: в поле адреса оставляем только «улицу + дом»,
    // город уезжает в родителя через колбэк.
    onChange(item.line1)
    onSuggestionPicked?.(item)
    clear()
    setShowList(false)
  }

  function handleBlur() {
    // small delay so click on suggestion registers first
    setTimeout(() => setShowList(false), 150)
  }

  return (
    <label className="relative flex flex-col text-left">
      {label ? (
        <span className="stryzh-input-label">
          {label}
          {required ? ' *' : ''}
        </span>
      ) : null}
      <div className="relative">
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => { if (value.length >= 3) setShowList(true) }}
          onBlur={handleBlur}
          className={clsx(
            'stryzh-input-field',
            error && 'stryzh-input-field--error',
          )}
        />
        {isConfigured && loading ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
            …
          </span>
        ) : null}
      </div>

      {error ? (
        <span className="stryzh-input-error-text mt-1">{error}</span>
      ) : null}

      {showSuggestions ? (
        // Не используем .stryzh-select-option — его flex/justify-between/align-center
        // ломает двустрочную раскладку (центрирует контент и мешает truncate).
        // Стилизуем карточку самостоятельно: левый текст, перенос внутри строки
        // через truncate, padding ужат, чтобы три-четыре подсказки помещались
        // в дровер без выхода за его пределы.
        <div className="stryzh-select-dropdown absolute top-full z-50 mt-1 w-full overflow-hidden">
          {suggestions.map((s) => (
            <button
              key={s.value}
              type="button"
              title={s.line2 ? `${s.line1}\n${s.line2}` : s.line1}
              className="block w-full min-w-0 cursor-pointer rounded-[var(--stryzh-radius-sm)] border-0 bg-transparent px-3 py-2 text-left transition-colors hover:bg-[color:var(--stryzh-color-button-ghost-hover-bg)]"
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleSelect(s)
              }}
            >
              <div className="truncate text-sm text-[color:var(--stryzh-color-text-primary)]">
                {s.line1}
              </div>
              {s.line2 ? (
                <div className="truncate text-xs text-[color:var(--stryzh-color-text-secondary)]">
                  {s.line2}
                </div>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}

      {!isConfigured ? (
        <p className="mt-1 text-[10px] text-[color:var(--stryzh-color-text-secondary)]">
          {t('addressInput.apiKeyHintPre')}<code>VITE_YANDEX_MAPS_API_KEY</code>{t('addressInput.apiKeyHintPost')}
        </p>
      ) : null}
    </label>
  )
}
